import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { LineSeparator } from '@/components/LineSeparator';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { isSelectableProduct, useSelectableProducts } from '@/hooks/use-available-products';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useProductSwitchModal } from '@/hooks/use-product-switch';
import {
    DESKTOP_PAGE_MARGIN,
    MAX_DESKTOP_WEB_WIDTH,
    PRODUCT_SWITCHER_HEIGHT,
} from '@/utils/constants-platform-specific';

import { DesktopProductTab } from './DesktopProductTab';

/**
 * Slim product switcher strip on top of the desktop web header
 */
export const DesktopProductSwitcherBar = () => {
    const { t } = useTranslation('product_switch_menu');
    const activeProduct = useJurisdictionStore(state => state.product);
    const { switchingAvailable, changeProduct } = useProductSwitchModal();
    const selectableProducts = useSelectableProducts();

    if (!switchingAvailable) {
        return null;
    }

    // Active product first, the rest in their configured order
    const products =
        isSelectableProduct(activeProduct) && selectableProducts.includes(activeProduct)
            ? [activeProduct, ...selectableProducts.filter(product => product !== activeProduct)]
            : selectableProducts;

    return (
        <Box
            height={PRODUCT_SWITCHER_HEIGHT}
            width="100%"
            style={styles.pageMargin}
            alignItems="center"
            backgroundColor={ds.colorToken.background.neutral.base}
            borderBottomWidth={1}
            borderBottomColor={ds.colorToken.border.strong}
        >
            <Row height="100%" width="100%" maxWidth={MAX_DESKTOP_WEB_WIDTH} alignItems="center" gap="s8">
                {products.map((product, index) => {
                    const isActive = product === activeProduct;
                    return (
                        <Fragment key={product}>
                            {index > 0 ? <LineSeparator vertical style={styles.divider} /> : null}
                            <Box style={index === 0 ? styles.firstTab : undefined}>
                                <DesktopProductTab
                                    label={t(product)}
                                    active={isActive}
                                    onPress={() => {
                                        if (!isActive) {
                                            changeProduct(product);
                                        }
                                    }}
                                    testID={`desktop-product-tab-${product}`}
                                />
                            </Box>
                        </Fragment>
                    );
                })}
            </Row>
        </Box>
    );
};

const styles = StyleSheet.create({
    divider: {
        height: 20,
    },
    // cancel the tab's own 8px left padding so the first tab's label stays left-aligned with the
    // logo in the nav bar below (its hover wash then reaches the strip's left edge, as in Figma)
    firstTab: {
        marginLeft: -8,
    },
    pageMargin: {
        paddingHorizontal: DESKTOP_PAGE_MARGIN,
    },
});
